import {
  DEFAULT_MAX_DEV_COUNT,
  DEFAULT_MAX_MEMBER_COUNT,
} from 'infra/constants'
import {
  ConditionForm,
  DashboardScoutRequestDetail,
  DashboardScoutRequestOneQueryParams,
  DashboardScoutRequestSearchFilterParams,
  EditableCareer,
  EditableOtherActivity,
  ScoutProfile,
} from 'types/common'

export const initialCareer: EditableCareer = {
  name: '',
  image: null,
  position: '',
  started: { year: '', month: '' },
  ended: { year: '', month: '' },
  isWorking: false,
  techStacks: [],
  description: '',
  isVerified: false,
  isCompleted: false,
  company: null,
  isStartup: false,
  serviceAreas: [],
  link: '',
  isCreatedByUser: true,
}

export const initialOtherActivity: EditableOtherActivity = {
  title: '',
  description: '',
  started: { year: '', month: '' },
  ended: { year: '', month: '' },
  isOnGoing: false,
  isCompleted: false,
  techStacks: [],
}

// GET 응답값 형식
export const initialFilter: DashboardScoutRequestSearchFilterParams = {
  id: 0,
  name: '',
  positions: [],
  techStacks: [],
  workLevels: [],
  minExperience: 0,
  maxExperience: 10,
  minSalary: 0,
  maxSalary: 10000,
  idpCertificates: [],
  isEducationCs: false,
  isEducationFinished: false,
  isJobInterested: false,
  hasIntroduction: false,
  hasAccessibleAttachment: false,
  shouldMatchTalentStartupRequired: false,
  keywords: [],
  orderBy: '-updatedByUserAt',
  isExcludeViewedTalent: false,
  jobInterestLevels: [],
  schoolGroups: [],
  schools: [],
  educationCategories: [],
  isNotWorking: false,
  languageSkills: [],
  locations: [],
  isRemoteWorkExcluded: false,
  hasStartupCareerHistory: false,
  fundingRounds: [],
  serviceAreas: [],
  careerServiceAreas: [],
  aiAssessmentConditions: [],
}

// POST 요청값 형식
export const initialFilterParams: DashboardScoutRequestOneQueryParams = {
  positions: [],
  techStacks: [],
  workLevels: [],
  minExperience: 0,
  maxExperience: 10,
  minSalary: 0,
  maxSalary: 10000,
  idpCertificates: [],
  isEducationCs: false,
  isEducationFinished: false,
  isJobInterested: false,
  hasIntroduction: false,
  hasAccessibleAttachment: false,
  shouldMatchTalentStartupRequired: false,
  keywords: [],
  orderBy: '-updatedByUserAt',
  isExcludeViewedTalent: false,
  jobInterestLevels: [],
  schoolGroups: [],
  schools: [],
  educationCategories: [],
  isNotWorking: false,
  languageSkills: '',
  locations: [],
  isRemoteWorkExcluded: false,
  hasStartupCareerHistory: false,
  fundingRounds: [],
  serviceAreas: [],
  careerServiceAreas: [],
  aiAssessmentConditions: [],
}

export const initialRequestForm: ConditionForm = {
  workLevel: null,
  jobInterestLevel: null,
  isActivelySeeker: false,
  locations: [],
  serviceAreas: [],
  memberCount: {
    min: 0,
    max: DEFAULT_MAX_MEMBER_COUNT,
  },
  devCount: {
    min: 0,
    max: DEFAULT_MAX_DEV_COUNT,
  },
  fundingRounds: [],
  salary: {
    min: 0,
    max: 10000,
  },
  miscOptions: {
    shouldCto: false,
    shouldSupervisor: false,
    shouldStockOption: false,
    remoteWorkPreference: null,
    isSalaryNegotiableWithStockOption: false,
  },
  requiredConditions: [
    { dataKey: 'locations', isRequired: false },
    { dataKey: 'serviceAreas', isRequired: false },
    { dataKey: 'fundingRounds', isRequired: false },
    { dataKey: 'memberCount', isRequired: false },
    { dataKey: 'devCount', isRequired: false },
    { dataKey: 'salary', isRequired: false },
    { dataKey: 'shouldSupervisor', isRequired: false },
    { dataKey: 'shouldCto', isRequired: false },
    { dataKey: 'shouldStockOption', isRequired: false },
    { dataKey: 'remoteWorkPreference', isRequired: false },
  ],
  lastSavedStep: 0,
}

export const initialScoutProfile: ScoutProfile = {
  id: 0,
  name: '',
  image: '',
  educations: [],
  languageSkills: [],
  experience: 0,
  careers: [],
  otherActivities: [],
  files: [],
  links: [],
  introduction: '',
  personalities: [],
  expectation: { description: '', keywords: [] },
  jobSearchingReason: '',
  isPublished: false,
  idpCertificates: [],
  idpScore: 0,
  idpGrade: 1,
  exposureRankingRatio: 0,
  blockedCompanies: [],
  updatedByUserAt: '',
  publishedAt: '',
  createdAt: '',
  hasAnalyzedResult: false,
  ...initialRequestForm,
}

export const initialDashboardScoutRequestDetail: DashboardScoutRequestDetail = {
  id: 0,
  name: '',
  image: '',
  birthYear: null,
  experience: 0,
  positions: [],
  techStacks: [],
  workLevel: null,
  jobInterestLevel: null,
  isActivelySeeker: false,
  idpCertificates: [],
  careers: [],
  otherActivities: [],
  introduction: '',
  personalities: [],
  expectation: { description: '', keywords: [] },
  jobSearchingReason: '',
  educations: [],
  languageSkills: [],
  files: [],
  links: [],
  locations: [],
  serviceAreas: [],
  fundingRounds: [],
  memberCount: null,
  devCount: null,
  salary: null,
  miscOptions: null,
  requiredConditions: null,
  isPublished: true,
  summary: null,
  assessMemo: null,
  saveLists: [],
  lastActiveAt: null,
  viewedMeAt: null,
  savedMeAt: null,
  viewedAt: null,
  lastListedAt: null,
  updatedAt: '',
  isProposed: false,
  proposalId: null,
  isCanceled: false,
  isRejected: false,
  isResumeAllowed: false,
  startupDismissed: false,
  hasConfirmedMeet: false,
  hasDoneMeet: false,
  status: null,
  hasTempScoutProposal: false,
  respondedCount: 0,
  receivedMessageCount: 0,
  hasPendingScoutProposal: false,
  hasApplication: false,
  curatedAt: null,
  isDeleted: false,
  aiAssessment: null,
  managerComment: null,
  isCommonCurated: false,
  commonComment: null,
}
